import { Spinner } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import * as R from "remeda";
import { useMemo } from "react";
import CONSTANTS from "core/constants";
import type { Progress } from "api/types";
import { useGetAllProgress } from "api/useIcbtQueries";
import { useQueryErrorLogger } from "hooks";
import { isForbiddenError } from "api/helpers";
import { DEFAULT_LANG } from "helpers/language";
import { useGetAllProgramsAndTreatmentsQuery } from "../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";

type LastIcbtActivityProps = {
  selectedPsychologistId: number | string | null | undefined;
  patientUserId: number;
  onClickLink: () => void;
};

export const LastIcbtActivity = ({
  selectedPsychologistId,
  onClickLink,
  patientUserId,
}: LastIcbtActivityProps): JSX.Element => {
  const { t } = useTranslation();

  // Fetch the icbt data and progress data from indy
  const {
    data: programsAndTreatments,
    isLoading: programsAndTreatmentsIsLoading,
    error: programsAndTreatmentsError,
  } = useGetAllProgramsAndTreatmentsQuery(DEFAULT_LANG);

  const {
    data: allProgress,
    isLoading: allProgressIsLoading,
    error: allProgressError,
  } = useGetAllProgress(patientUserId.toString());

  // Derive loading status
  const isLoading = programsAndTreatmentsIsLoading || allProgressIsLoading;

  // Check for errors, derive error status and log if there are errors
  const possiblyErrors = {
    programsAndTreatmentsError,
    allProgressError,
  };

  const isError = useQueryErrorLogger(
    possiblyErrors,
    "Failed to load data in LastIcbtActivity",
    {
      team: "dna",
      tag: "assignIcbt",
      patientUserId,
    },
  );

  // Derive data
  const latestViewedProgram = useMemo(() => {
    if (!allProgress || !programsAndTreatments) return null;
    const latestProgress = R.pipe(
      Object.values(allProgress),
      R.filter(
        <P extends Progress>(
          p: P,
        ): p is P & Pick<Required<P>, "lastProgressAt"> =>
          Boolean(p.lastProgressAt),
      ),
      R.sortBy(R.prop("lastProgressAt")),
      R.last(),
    );
    if (!latestProgress) return null; // empty progress
    return programsAndTreatments.programs[latestProgress.programId];
  }, [allProgress, programsAndTreatments]);

  if (isError) {
    return (
      <Typography variant="body-small">
        {Object.values(possiblyErrors).some(isForbiddenError)
          ? t("ICBT_LOADING_DATA_ERROR_FORBIDDEN")
          : t("ICBT_LOADING_DATA_ERROR")}
      </Typography>
    );
  }
  if (isLoading) {
    return <Spinner />;
  }

  if (!latestViewedProgram)
    return <Typography variant="body-small">{t("NOT_STARTED")}</Typography>;

  const psychologistId = selectedPsychologistId ?? null;
  const patientCardIcbtTabLink = `/patients/psychologist/${psychologistId}/patient/${patientUserId}/${CONSTANTS.PATIENT_CARD_TABS.ICBT.toLowerCase()}`;

  const { title, subtitle } = (() => {
    if (latestViewedProgram.standalone) {
      return { title: latestViewedProgram.title, subtitle: "" };
    }
    return {
      title: latestViewedProgram.treatment.title,
      subtitle: latestViewedProgram.title,
    };
  })();

  return (
    <div>
      <StyledLink onClick={onClickLink} to={patientCardIcbtTabLink}>
        <Typography variant="body">{title}</Typography>
      </StyledLink>
      <Typography variant="body-small">{subtitle}</Typography>
    </div>
  );
};

const StyledLink = styled(Link)`
  text-decoration: underline;
  overflow-wrap: break-word;
`;
